import { useCanSign } from './useConsultationAccess';
import { useDelegationDoctorIds } from './useConsentedDoctorIds';
import type { MedicalConsultation } from '../../../types/api';

/**
 * clinical-record-signing R8: a draft must never reach a printable page,
 * whether the request goes through the server's `generatePdf` gate or (as
 * `ConsultationPrintPage`/`PrescriptionPrintPage` do) a client-side
 * `window.print()` over the plain `GET` response. This hook is the client
 * side of that gate — it never trusts the print pages to remember to check
 * `status` themselves, and fails closed (blocks) when `status` is missing,
 * same reasoning as the backend's `draft` DB default.
 *
 * `onSigned` lets the caller swap its local consultation for the freshly
 * signed one, which flips `isBlocked` to false and lets the page's own
 * print effect fire.
 */
export function usePrintGate(
  consultation: MedicalConsultation | null | undefined,
  onSigned: (updated: MedicalConsultation) => void,
) {
  // An assistant whose delegated signature is in effect may sign their own draft here too (R12).
  const { delegatedSignerDoctorIds } = useDelegationDoctorIds();
  const { canSign, signing, error: signError, sign } = useCanSign(consultation, onSigned, delegatedSignerDoctorIds);
  const isBlocked = !!consultation && (consultation.status === undefined || consultation.status === 'draft');

  return { isBlocked, canSign, signing, signError, sign };
}
